// src/marketing/budget/DailyExpensesSection.tsx
import React, { useState } from 'react'
import { ApiClientError } from '../../lib/api'
import { EXPENSE_CATEGORY_SUGGESTIONS } from '../../lib/types'
import type { BudgetLine } from '../../lib/types'
import {
  useDailyExpenses, useCreateDailyExpense, useDeleteDailyExpense,
} from './useDailyExpenses'
import { ExpenseChart } from './ExpenseChart'

const PLATFORM_SUGGESTIONS = ['apple_search_ads', 'meta', 'tiktok']

function todayIso(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

type QuickForm = { expenseDate: string; amount: string; category: string; platform: string; note: string }

export function DailyExpensesSection({ appId, budgetLines }: { appId: string | null; budgetLines: BudgetLine[] }) {
  const { data: expenses = [], isLoading } = useDailyExpenses(appId)
  const createExpense = useCreateDailyExpense()
  const deleteExpense = useDeleteDailyExpense()

  const [form, setForm] = useState<QuickForm>({
    expenseDate: todayIso(), amount: '', category: '', platform: '', note: '',
  })
  const [error, setError] = useState<string | null>(null)

  const categorySuggestions = [
    ...new Set([...EXPENSE_CATEGORY_SUGGESTIONS, ...budgetLines.map((l) => l.category)]),
  ]

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!appId || !form.amount || !form.category.trim()) return
    setError(null)
    createExpense.mutate(
      {
        appId,
        expenseDate: form.expenseDate,
        amount: parseFloat(form.amount),
        category: form.category.trim(),
        platform: form.platform.trim() || null,
        note: form.note.trim() || null,
      },
      {
        onSuccess: () =>
          setForm((f) => ({ expenseDate: todayIso(), amount: '', category: f.category, platform: '', note: '' })),
        onError: (err) => setError(err instanceof ApiClientError ? err.message : 'Erreur inattendue'),
      },
    )
  }

  return (
    <section data-testid="expenses-section">
      <div style={sectionHeadStyle}>
        <div>
          <p style={sectionLabelStyle}>Quotidien</p>
          <h2 style={sectionTitleStyle}>Dépenses quotidiennes</h2>
        </div>
      </div>

      {/* Saisie rapide */}
      <form data-testid="expense-quick-add" onSubmit={handleSubmit}
        style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end', padding: '0.875rem 0', marginBottom: '0.75rem' }}>
        {error && (
          <div style={{ width: '100%', color: 'var(--arancio)', fontSize: '0.8125rem' }}>{error}</div>
        )}
        <div>
          <label style={labelStyle} htmlFor="exp-date">Date</label>
          <input id="exp-date" type="date" required value={form.expenseDate}
            onChange={(e) => setForm((f) => ({ ...f, expenseDate: e.target.value }))} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle} htmlFor="exp-amount">Montant (€)</label>
          <input id="exp-amount" type="number" min="0.01" step="0.01" required value={form.amount}
            onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} placeholder="25" style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle} htmlFor="exp-category">Catégorie</label>
          <input id="exp-category" list="exp-category-list" required value={form.category}
            onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} placeholder="ads" style={inputStyle} />
          <datalist id="exp-category-list">
            {categorySuggestions.map((c) => <option key={c} value={c} />)}
          </datalist>
        </div>
        <div>
          <label style={labelStyle} htmlFor="exp-platform">Plateforme</label>
          <input id="exp-platform" list="exp-platform-list" value={form.platform}
            onChange={(e) => setForm((f) => ({ ...f, platform: e.target.value }))} placeholder="—" style={inputStyle} />
          <datalist id="exp-platform-list">
            {PLATFORM_SUGGESTIONS.map((p) => <option key={p} value={p} />)}
          </datalist>
        </div>
        <div style={{ minWidth: '180px' }}>
          <label style={labelStyle} htmlFor="exp-note">Note</label>
          <input id="exp-note" value={form.note}
            onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} style={inputStyle} />
        </div>
        <button type="submit" style={btnPrimary} disabled={createExpense.isPending}>
          {createExpense.isPending ? 'Ajout…' : 'Ajouter'}
        </button>
      </form>

      {/* Courbe + totaux */}
      <ExpenseChart expenses={expenses} />

      {/* Journal (30 dernières) */}
      <div className="twrap">
        {isLoading ? (
          <p style={{ padding: '1rem', color: 'var(--muted)', fontSize: '0.875rem' }}>Chargement…</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Montant</th>
                <th style={thStyle}>Catégorie</th>
                <th style={thStyle}>Plateforme</th>
                <th style={thStyle}>Note</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {expenses.length === 0 && (
                <tr><td colSpan={6} style={{ ...tdStyle, color: 'var(--muted)', textAlign: 'center' }}>
                  Aucune dépense enregistrée.
                </td></tr>
              )}
              {expenses.slice(0, 30).map((e) => (
                <tr key={e.id} data-testid={`expense-row-${e.id}`} style={{ borderTop: '1px solid var(--line)' }}>
                  <td style={tdStyle}>{e.expenseDate}</td>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{e.amount.toFixed(2)} €</td>
                  <td style={tdStyle}>{e.category}{e.source === 'deal_auto' && (
                    <span style={{ marginLeft: '0.375rem', fontSize: '0.6875rem', color: 'var(--bronze)', border: '1px solid var(--line)', borderRadius: '4px', padding: '0.0625rem 0.3125rem' }}>auto</span>
                  )}</td>
                  <td style={{ ...tdStyle, color: 'var(--muted)' }}>{e.platform ?? '—'}</td>
                  <td style={{ ...tdStyle, color: 'var(--muted)' }}>{e.note ?? '—'}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>
                    <button
                      onClick={() => {
                        if (!window.confirm('Supprimer cette dépense ?')) return
                        setError(null)
                        deleteExpense.mutate(e.id, {
                          onError: (err) =>
                            setError(err instanceof ApiClientError ? err.message : 'Erreur inattendue'),
                        })
                      }}
                      style={{ ...btnIconStyle, color: 'var(--arancio)' }}
                    >
                      Supprimer
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  )
}

// Styles — recopiés de ChannelsBudgetPage.tsx (constantes non exportées en fin de fichier)
const sectionHeadStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.75rem',
}
const sectionLabelStyle: React.CSSProperties = {
  margin: 0, fontFamily: 'var(--mono)', fontSize: '10px', letterSpacing: '.2em',
  textTransform: 'uppercase', color: 'var(--bronze)',
}
const sectionTitleStyle: React.CSSProperties = {
  margin: '4px 0 0', fontWeight: 800, fontSize: '1.1rem',
  textTransform: 'uppercase', letterSpacing: '.01em',
}
const inputStyle: React.CSSProperties = {
  background: 'var(--bg)', border: '1px solid var(--line)', borderRadius: '6px',
  padding: '0.375rem 0.625rem', fontSize: '0.875rem', color: 'var(--fg)',
  fontFamily: 'inherit', width: '100%',
}
const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: '0.75rem', color: 'var(--muted)', marginBottom: '0.25rem',
}
const thStyle: React.CSSProperties = {
  fontFamily: 'var(--mono)', fontSize: '10.5px', letterSpacing: '.1em',
  textTransform: 'uppercase', color: 'var(--bronze)', textAlign: 'left',
  padding: '11px 13px', background: 'var(--surface)', borderBottom: '1px solid var(--line)',
  whiteSpace: 'nowrap',
}
const tdStyle: React.CSSProperties = {
  padding: '10px 13px', color: 'var(--fg)', verticalAlign: 'top',
}
const btnPrimary: React.CSSProperties = {
  background: 'var(--arancio)', color: '#fff', border: 'none', borderRadius: '6px',
  padding: '0.4375rem 1rem', fontSize: '0.875rem', fontWeight: 600,
  fontFamily: 'inherit', cursor: 'pointer',
}
const btnIconStyle: React.CSSProperties = {
  background: 'none', border: 'none', color: 'var(--arancio)', cursor: 'pointer',
  fontSize: '0.8125rem', fontFamily: 'inherit', padding: '0.125rem 0.25rem',
}
